import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, MapPin, Sparkles, Mail } from 'lucide-react';
import { Image } from '@/components/ui/image';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Reveal from '@/components/Reveal';
import Flag from '@/components/Flag';
import { ROSTER, getTalent } from '@/data/roster';
import { CONTACT } from '@/data/contact';

export default function TalentProfile() {
  const { slug } = useParams();
  const m = getTalent(slug);

  if (!m) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-40 pb-24 text-center">
          <h1 className="font-display font-bold text-3xl text-white">Profile not found</h1>
          <Link to="/#roster" className="mt-6 inline-flex items-center gap-2 text-[#3B82F6] hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to the roster
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const others = ROSTER.filter((r) => r.slug !== m.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 fine-grid opacity-60" />
        <div className="absolute top-0 right-0 w-2/3 h-full bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.16),transparent_58%)]" />

        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <Link
            to="/#roster"
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#3B82F6] transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Our Roster
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] order-1 lg:order-2"
            >
              <Image
                src={m.img}
                alt={`${m.name} — ${m.title}`}
                fittingType="fill"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/70 via-transparent to-transparent" />
            </motion.div>

            <div className="order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2.5 mb-6 px-4 py-2 rounded-lg border border-[#3B82F6]/30 bg-[#2563EB]/10"
              >
                <MapPin className="w-3.5 h-3.5 text-[#3B82F6]" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">
                  {m.city}, {m.country}
                </span>
                <Flag code={m.code} className="w-5 h-3.5" />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display font-bold text-white tracking-tight leading-[1.05]"
                style={{ fontSize: 'clamp(2.2rem, 4.6vw, 4rem)' }}
              >
                {m.name}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.25 }}
                className="mt-4 text-[#3B82F6] font-display font-semibold text-lg lg:text-xl"
              >
                {m.title}
              </motion.p>

              <motion.a
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                href={`mailto:${m.email}`}
                className="mt-5 inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-white/15 bg-white/5 text-sm text-white/75 hover:border-[#3B82F6] hover:text-[#3B82F6] transition-all duration-300"
              >
                <Mail className="w-4 h-4" />
                {m.email}
              </motion.a>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mt-6 text-white/60 leading-relaxed font-light text-base lg:text-lg max-w-xl"
              >
                {m.bio}
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.45 }}
                className="mt-4 text-white/50 leading-relaxed font-light max-w-xl"
              >
                {m.bio2}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.55 }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <a
                  href={`mailto:${CONTACT.bookings}?cc=${m.email}&subject=Booking enquiry — ${m.name}`}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors blue-glow"
                >
                  Book {m.name.split(' ')[0]}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  to="/#roster"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg text-sm font-semibold border border-white/20 text-white hover:border-white/50 transition-colors"
                >
                  View Full Roster
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {m.stats.map((s, i) => (
            <Reveal key={s.k} delay={i * 0.08}>
              <div>
                {s.markets ? (
                  <div className="flex items-center gap-2.5 h-[36px] lg:h-[42px]">
                    {s.markets.map((c) => (
                      <Flag key={c} code={c} className="w-8 h-[22px]" />
                    ))}
                  </div>
                ) : (
                  <div className="font-display font-bold text-2xl lg:text-3xl text-white">{s.v}</div>
                )}
                <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mt-1.5">{s.k}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DETAIL */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2563EB] mb-4 block">
              Profile
            </span>
            <h2 className="font-display font-bold text-[#0B0B0B] tracking-tight text-3xl lg:text-4xl">
              Defining Characteristics
            </h2>
            <div className="mt-8 rounded-lg border border-[#0B0B0B]/10 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 bg-[#F5F5F5] border-b border-[#0B0B0B]/10">
                <span className="text-sm text-[#0B0B0B]/50">Age</span>
                <span className="text-sm font-semibold text-[#0B0B0B]">{m.age}</span>
              </div>
              {Object.entries(m.attributes).map(([k, v], i, arr) => (
                <div
                  key={k}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-6 py-4 ${
                    i < arr.length - 1 ? 'border-b border-[#0B0B0B]/10' : ''
                  }`}
                >
                  <span className="text-sm text-[#0B0B0B]/50">{k}</span>
                  <span className="text-sm font-medium text-[#0B0B0B] sm:text-right sm:max-w-[60%]">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2563EB] mb-4 block">
              Commercial Fit
            </span>
            <h2 className="font-display font-bold text-[#0B0B0B] tracking-tight text-3xl lg:text-4xl">
              Audience & Brand Alignment
            </h2>

            <div className="mt-8 rounded-lg bg-[#F5F5F5] border border-[#0B0B0B]/10 p-7">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#2563EB]" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B0B0B]/50">
                  Niche Appeal
                </span>
              </div>
              <p className="text-[#0B0B0B]/70 leading-relaxed font-light">{m.appeal}</p>
            </div>

            <div className="mt-6">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B0B0B]/45 block mb-3">
                Core Specialisation
              </span>
              <p className="text-[#0B0B0B]/75">{m.specialization}</p>
            </div>

            <div className="mt-6">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B0B0B]/45 block mb-3">
                Direct Enquiries
              </span>
              <a
                href={`mailto:${m.email}`}
                className="inline-flex items-center gap-2 text-[#0B0B0B]/80 hover:text-[#2563EB] transition-colors"
              >
                <Mail className="w-4 h-4 text-[#2563EB]" />
                <span className="text-sm font-medium">{m.email}</span>
              </a>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B0B0B]/45 block mb-3">
                  Active Platforms
                </span>
                <div className="flex flex-wrap gap-2">
                  {m.platforms.map((p) => (
                    <span key={p} className="px-3 py-1.5 rounded-lg bg-[#0B0B0B] text-white text-xs font-medium">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B0B0B]/45 block mb-3">
                  Brand Verticals
                </span>
                <ul className="space-y-2">
                  {m.verticals.map((v) => (
                    <li key={v} className="flex items-center gap-2.5 text-sm text-[#0B0B0B]/70">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0" />
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OTHERS */}
      <section className="bg-[#0B0B0B] py-20 lg:py-24">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 gap-6">
            <h2 className="font-display font-bold text-white tracking-tight text-2xl lg:text-3xl">
              More from the Roster
            </h2>
            <Link to="/#roster" className="inline-flex items-center gap-2 text-sm text-[#3B82F6] hover:text-white transition-colors shrink-0">
              All creators <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/talent/${o.slug}`}
                className="group rounded-lg overflow-hidden border border-white/10 bg-[#121212] hover:border-[#3B82F6]/60 transition-all duration-500"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <Image
                    src={o.img}
                    alt={o.name}
                    fittingType="fill"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <Flag code={o.code} className="w-[18px] h-3" />
                    <h3 className="font-display font-semibold text-sm text-white truncate group-hover:text-[#3B82F6] transition-colors">
                      {o.name}
                    </h3>
                  </div>
                  <p className="text-[11px] text-white/40 mt-1 truncate">{o.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}